import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Button,
  Container,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { Socket } from 'socket.io-client';
import { Game } from '@par/shared/types/game';
import { Profile } from '@par/shared/types/player';
import { GameDataFromServer, WhosOnline } from '../room/types';
import { ImageDisplay } from '../../dialog/ImageDisplay';
import { CustomMessageDialog } from '../../dialog/CustomMessageDialog';
import { LobbyTable } from './LobbyTable';
import './styles.css';
import { Mode } from '@par/shared/common/types';

type PropTypes = {
  clearGameDataFromServer: () => void;
  gameDataFromServer: GameDataFromServer;
  handleJoinGameClick: (gameId: string, mode: Mode) => void;
  handleLeaveGameClick: (gameId: string, profileId: string) => void;
  handlePickTeamsClick: (gameId: string) => void;
  handleRejoinGameClick: (gameId: string) => void;
  handleSpectateClick: (gameId: string) => void;
  lobbyGames?: Game[];
  lobbyGamesHistory?: Game[];
  onlinePlayer: WhosOnline;
  openTeamPickingDialog?: boolean;
  profile: Profile;
  socket?: Socket;
};

export const Lobby = ({
  clearGameDataFromServer,
  gameDataFromServer,
  handleJoinGameClick,
  handleLeaveGameClick,
  handlePickTeamsClick,
  handleRejoinGameClick,
  handleSpectateClick,
  lobbyGames,
  lobbyGamesHistory,
  onlinePlayer,
  openTeamPickingDialog,
  profile,
  socket,
}: PropTypes) => {
  const location = useLocation();
  const [recentLobbyGames, setRecentLobbyGames] = useState<Game[]>();
  const [loadingGames, setLoadingGames] = useState(true);
  const [openTourneyImage, setOpenTourneyImage] = useState(false);
  const [tourneyImgSource, setTourneyImgSource] = useState('');
  const [openCustomMessage, setOpenCustomMessage] = useState(false);
  const [customMessage, setCustomMessage] = useState<{
    message: string;
    title: string;
  }>();

  useEffect(() => {
    if (!lobbyGames) return;
    const combinedGames = lobbyGamesHistory
      ? lobbyGamesHistory.concat(lobbyGames)
      : lobbyGames;
    setRecentLobbyGames(
      combinedGames?.sort((a, b) => b.createdAt - a.createdAt)
    );
    setLoadingGames(false);
  }, [lobbyGames, lobbyGamesHistory]);

  useEffect(() => {
    if (!socket?.connected) {
      const joinedGame = recentLobbyGames?.find(
        (g) => g.gameId === gameDataFromServer?.gameId
      );
      if (gameDataFromServer?.gameId && joinedGame && !joinedGame.gameStarted) {
        clearGameDataFromServer();
        setCustomMessage({
          message: 'If you were in a game, you will have to rejoin it',
          title: 'You were disconnected',
        });
        setOpenCustomMessage(true);
      }
    }
  }, [socket]);

  const handleRemoveGameClick = (game: Game) => {
    socket.emit('deleteGame', {
      profileId: profile.id,
      gameId: game.gameId,
    });
  };

  const handleForceStartClick = (game: Game) => {
    socket.emit('forceStartSitAndGo', {
      gameId: game.gameId,
      profileId: profile.id,
    });
  };

  const handleDisplayTourneyFormat = () => {
    setTourneyImgSource(
      'https://res.cloudinary.com/dqlqwdqs9/image/upload/v1675673566/Tourney_Flow_1_pmrwwd.png'
    );
    setOpenTourneyImage(true);
  };

  const handleDisplayTourneyRules = () => {
    setTourneyImgSource(
      'https://res.cloudinary.com/dqlqwdqs9/image/upload/v1675673564/Tourney_Rules_1_uzgukz.png'
    );
    setOpenTourneyImage(true);
  };

  const handleDisplayTourneyGroupings = () => {
    setTourneyImgSource(
      'https://res.cloudinary.com/dqlqwdqs9/image/upload/v1675729643/Dark_Blue_Illustration_Steak_House_Price_List_1_jr6k5c.png'
    );
    setOpenTourneyImage(true);
  };

  const modals = (
    <>
      <CustomMessageDialog
        close={() => setOpenCustomMessage(false)}
        message={customMessage?.message}
        open={openCustomMessage}
        title={customMessage?.title}
      ></CustomMessageDialog>
      <ImageDisplay
        closeImageDisplay={() => setOpenTourneyImage(false)}
        imgSource={tourneyImgSource}
        openImageDisplay={openTourneyImage}
      ></ImageDisplay>
    </>
  );

  return (
    <>
      {modals}
      <Container sx={{ mt: 5, paddingBottom: 20 }}>
        {!loadingGames ? (
          <>
            {location.pathname === '/tourney-lobby' ? (
              <Stack
                direction={'row'}
                spacing={4}
                width="100%"
                padding={1}
                border="1px solid"
                borderColor={'tertiary.light'}
                borderRadius=".25rem"
                marginBottom={2}
              >
                <Button
                  sx={{ color: 'tertiary.light' }}
                  onClick={handleDisplayTourneyFormat}
                >
                  Tourney Format
                </Button>
                <Button
                  sx={{ color: 'tertiary.light' }}
                  onClick={handleDisplayTourneyRules}
                >
                  Tourney Rules
                </Button>
                <Button
                  sx={{ color: 'tertiary.light' }}
                  onClick={handleDisplayTourneyGroupings}
                >
                  Tourney Groupings
                </Button>
              </Stack>
            ) : null}
            <LobbyTable
              handleForceStartClick={handleForceStartClick}
              handleJoinGameClick={handleJoinGameClick}
              handleLeaveGameClick={handleLeaveGameClick}
              handlePickTeamsClick={handlePickTeamsClick}
              handleRejoinGameClick={handleRejoinGameClick}
              handleRemoveGameClick={handleRemoveGameClick}
              handleSpectateClick={handleSpectateClick}
              lobbyGames={recentLobbyGames}
              onlinePlayer={onlinePlayer}
              openTeamPickingDialog={openTeamPickingDialog}
              profile={profile}
              socket={socket}
            />
          </>
        ) : (
          <Stack>
            <Typography variant="h6led" textAlign={'center'} mt={5} mb={5}>
              Loading lobby...
            </Typography>
            <LinearProgress />
          </Stack>
        )}
      </Container>
    </>
  );
};
